// App.js
import React, {
  useState,
  useMemo,
  useEffect,
  useContext,
  createContext,
} from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import io from 'socket.io-client';
import Homepage from './Pages/Homepage';
import Login from './Pages/Login';
import Trades from './Pages/Trades';
import Orders from './Pages/Orders';
import OrderPage from './Pages/OrderPage';
import Clients from './Pages/Clients';
import Strategy from './Pages/Strategy';
import ClientPage from './Pages/ClientPage';
import CalendarEvents from './Pages/CalendarEvents';
import AdminPanel from './Pages/AdminPanel';
import UserPanel from './Pages/UserPanel';
import PrivateRoute  from './AuthComponents/PrivateRoute';
import LoginRoute  from './AuthComponents/LoginRoute';
import AdminRoute  from './AuthComponents/AdminRoute';
import Sidebar from './Components/Sidebar';
import { AuthLoginInfo }  from './AuthComponents/AuthLogin';
import configData from './Pages/config.json';

// Context holds both socket and live data
export const SocketDataContext = createContext(null);

function SocketDataProvider({ children }) {
  const [ltpData, setLtpData] = useState(null);
  const [livePnl, setLivePnl] = useState(0);
  const [totalPnl, setTotalPnl] = useState(0);
  const [stratgPnl, setStratgPnl] = useState(null);

  // single shared socket for the whole app
  const socket = useMemo(
    () =>
      io(configData.SOCKET_URI, {
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 500,
        reconnectionDelayMax: 3000,
        timeout: 20000,
        transports: ['websocket'],
      }),
    []
  );

  useEffect(() => {
    const handleMessage = (raw) => {
      let data = raw;
      if (typeof raw === 'string') {
        try {
          data = JSON.parse(raw);
        } catch {
          return;
        }
      }

      if (data.event === 'ltp_data') {
        // mirror your old WS logic here
        setLtpData(data.ltp || null);

        if (data.live_pnl) {
          setLivePnl(data.live_pnl);
        } else {
          setLivePnl(0);
        }

        if (data.total_pnl) {
          setTotalPnl(data.total_pnl);
          document.title = `MTM ${data.total_pnl}`;
        } else {
          setTotalPnl(0);
        }

        if (data.stratg_pnl) {
          setStratgPnl(data.stratg_pnl);
        }
      }

      // you can also handle other events here if you want
    };

    socket.on('message', handleMessage);

    return () => {
      socket.off('message', handleMessage);
    };
  }, [socket]);
  
  /*
  useEffect(() => {
    if (typeof document === "undefined") return;

    // Store the timeout ID
    let timer = setTimeout(() => {
      document.title = `PNL: ${totalPnl}`;
    }, 100); // update once per second

    // Clear previous timer if pnl keeps changing
    return () => clearTimeout(timer);
  }, [totalPnl]); 
  */

  const value = {
    socket,
    ltpData,
    livePnl,
    totalPnl,
    stratgPnl,
  };

  return (
    <SocketDataContext.Provider value={value}>
      {children}
    </SocketDataContext.Provider>
  );
}

function App() {
  const ctx = useContext(AuthLoginInfo);

  return (
    <SocketDataProvider>
      <BrowserRouter>
        <Sidebar />
        <Routes>
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Homepage />
              </PrivateRoute>
            }
          />
          <Route
            path="/trades"
            element={
              <PrivateRoute>
                <Trades />
              </PrivateRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <PrivateRoute>
                <Orders />
              </PrivateRoute>
            }
          />
          <Route
            path="/orders/:orderId"
            element={
              <PrivateRoute>
                <OrderPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/strategy"
            element={
              <PrivateRoute>
                <Strategy />
              </PrivateRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <PrivateRoute>
                <Clients />
              </PrivateRoute>
            }
          />
          <Route
            path="/clients/:clientId"
            element={
              <PrivateRoute>
                <ClientPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <PrivateRoute>
                <CalendarEvents />
              </PrivateRoute>
            }
          />
          <Route
            path="/adminPannel"
            element={
              <AdminRoute>
                <AdminPanel />
              </AdminRoute>
            }
          />
          <Route
            path="/userPannel"
            element={
              <PrivateRoute>
                <UserPanel />
              </PrivateRoute>
            }
          />
          <Route
            path="/login"
            element={
              <LoginRoute>
                <Login />
              </LoginRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </SocketDataProvider>
  );
}

export default App;